import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Chip,
    Divider,
    useTheme,
    Paper,
    Stack,
    Badge,
    LinearProgress,
} from '@mui/material';
import {
    SatelliteAlt as SatelliteIcon,
    GpsFixed as GpsIcon,
    SignalCellularAlt as SignalIcon,
    AccessTime as TimeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Shield as ShieldIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
    Device,
    InterferenceDetail,
    IntegritySource,
    UbxFlagsSource,
    UbxFlagsPerDevice,
    AgcDetectionSource,
    AgcPerDevice,
} from '../../types/Device';
import { useDeviceRealtime } from '../../hooks/useDeviceRealtime';

interface GnssReceiversTabProps {
    device: Device;
}

interface FixStatusData {
    fix_status: {
        fix_type: number;
        fix_type_name: string;
        fix_valid: boolean;
        differential_solution: boolean;
        carrier_solution_valid: boolean;
        carrier_solution: number;
        spoofing_state: number;
        spoofing_state_name: string;
        time_to_first_fix: number;
        time_since_startup: number;
        timestamp: string;
    };
}

interface ConstellationData {
    constellation_summary: {
        total_satellites: number;
        satellites_used: number;
        constellation_counts: {
            GPS: number;
            Galileo: number;
            BeiDou: number;
            QZSS?: number;
            GLONASS?: number;
        };
        satellites_used_by_constellation: {
            GPS: number;
            Galileo: number;
            BeiDou: number;
            QZSS?: number;
            GLONASS?: number;
        };
        usage_percentage: number;
    };
}

const GnssReceiversTab: React.FC<GnssReceiversTabProps> = ({ device }) => {
    const theme = useTheme();
    const { realtimeData } = useDeviceRealtime(device.id);

    // Interference helpers
    const getInterferenceSource = <T extends InterferenceDetail>(sourceType: string): T | undefined => {
        const details = realtimeData.telemetry?.interference_details;
        if (!Array.isArray(details)) return undefined;
        return details.find(d => d.source === sourceType) as T | undefined;
    };

    const getStateColor = (state: number): string => {
        switch (state) {
            case 0: return theme.palette.grey[500];
            case 1: return theme.palette.success.main;
            case 2: return theme.palette.warning.main;
            case 3: return theme.palette.error.main;
            default: return theme.palette.grey[500];
        }
    };

    const getJammingLabel = (state: number): string => {
        switch (state) {
            case 0: return 'Unknown';
            case 1: return 'OK';
            case 2: return 'Warning';
            case 3: return 'Critical';
            default: return 'Unknown';
        }
    };

    const getSpoofingLabel = (state: number): string => {
        switch (state) {
            case 0: return 'Unknown';
            case 1: return 'No indication';
            case 2: return 'Indicated';
            case 3: return 'Affirmed';
            default: return 'Unknown';
        }
    };

    const getJamIndColor = (value: number): string => {
        if (value <= 30) return theme.palette.success.main;
        if (value <= 100) return theme.palette.warning.main;
        if (value <= 200) return theme.palette.warning.dark;
        return theme.palette.error.main;
    };

    const getAgcClassificationColor = (classification: string | null): string => {
        if (!classification) return theme.palette.success.main;
        if (classification.includes('Very low')) return theme.palette.info.main;
        if (classification.includes('Low-power')) return theme.palette.warning.main;
        if (classification.includes('Medium-power')) return theme.palette.warning.dark;
        if (classification.includes('High-power')) return theme.palette.error.main;
        return theme.palette.grey[500];
    };

    // Helper to get fix status description based on fix_type number
    const getFixStatusDescription = (fixType: number): { label: string, color: 'success' | 'warning' | 'error' | 'default' } => {
        switch (fixType) {
            case 0:
                return { label: 'No Fix', color: 'error' };
            case 1:
                return { label: 'Dead Reckoning', color: 'warning' };
            case 2:
                return { label: '2D Fix', color: 'warning' };
            case 3:
                return { label: '3D Fix', color: 'success' };
            case 4:
                return { label: 'GNSS + Dead Reckoning', color: 'success' };
            case 5:
                return { label: 'Time Only', color: 'warning' };
            default:
                return { label: 'Unknown', color: 'default' };
        }
    };

    // Helper to format constellation summary
    const formatConstellationSummary = (data: ConstellationData) => {
        const summary = data.constellation_summary;
        const total = summary.total_satellites || 0;
        const used = summary.satellites_used || 0;
        const counts = summary.constellation_counts || {};
        
        return (
            <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Satellites in View:
                    </Typography>
                    <Typography variant="h6" color="primary">
                        {total}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Used in Fix:
                    </Typography>
                    <Typography variant="h6" color="success.main">
                        {used}
                    </Typography>
                </Box>
                <Divider />
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">GPS:</Typography>
                        <Typography variant="body2">{counts.GPS || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">GLONASS:</Typography>
                        <Typography variant="body2">{counts.GLONASS || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Galileo:</Typography>
                        <Typography variant="body2">{counts.Galileo || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">BeiDou:</Typography>
                        <Typography variant="body2">{counts.BeiDou || 0}</Typography>
                    </Grid>
                </Grid>
            </Stack>
        );
    };

    // Render GNSS device card
    const renderGnssDeviceCard = (gnssDeviceId: number) => {
        const ubxData = realtimeData.ubx_telemetry?.[gnssDeviceId];
        
        // Safe parsing of NAV-STATUS data
        let navStatusData: FixStatusData | undefined;
        try {
            const rawNavStatus = ubxData?.['NAV-STATUS']?.data?.parsed_data;
            if (rawNavStatus && typeof rawNavStatus === 'object' && 'fix_status' in rawNavStatus) {
                navStatusData = rawNavStatus as FixStatusData;
            }
        } catch (error) {
            console.warn('Error parsing NAV-STATUS data:', error);
            navStatusData = undefined;
        }
        
        // Safe parsing of NAV-SAT data
        let navSatData: ConstellationData | undefined;
        try {
            const rawNavSat = ubxData?.['NAV-SAT']?.data?.parsed_data;
            if (rawNavSat && typeof rawNavSat === 'object' && 'constellation_summary' in rawNavSat) {
                navSatData = rawNavSat as ConstellationData;
            }
        } catch (error) {
            console.warn('Error parsing NAV-SAT data:', error);
            navSatData = undefined;
        }
        
        const hasData = ubxData && (navStatusData || navSatData);
        const lastUpdate = ubxData ? Object.values(ubxData).map(msg => msg.timestamp).sort().pop() : null;

        return (
            <Grid item xs={12} md={6} lg={6} key={gnssDeviceId}>
                <Card 
                    variant="outlined" 
                    sx={{ 
                        height: '100%',
                        border: hasData ? '2px solid' : '1px solid',
                        borderColor: hasData ? 'success.main' : 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[4],
                        },
                    }}
                >
                    <CardContent>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SatelliteIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                <Typography variant="h6" fontWeight={600}>
                                    GNSS Device {gnssDeviceId}
                                </Typography>
                            </Box>
                            {hasData && (
                                <Chip
                                    label="LIVE"
                                    size="small"
                                    color="success"
                                    sx={{ fontSize: '0.7rem', height: '20px' }}
                                />
                            )}
                        </Box>

                        {hasData ? (
                            <Box>
                                {/* Fix Status Section */}
                                {navStatusData && (
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 2, 
                                            mb: 2, 
                                            backgroundColor: theme.palette.background.default,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <GpsIcon sx={{ mr: 1, color: theme.palette.secondary.main }} />
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                Fix Status
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Status:
                                            </Typography>
                                            <Chip
                                                label={getFixStatusDescription(navStatusData?.fix_status?.fix_type ?? 0).label}
                                                color={getFixStatusDescription(navStatusData?.fix_status?.fix_type ?? 0).color}
                                                size="small"
                                            />
                                        </Box>

                                        <Grid container spacing={1}>
                                            <Grid item xs={4}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    {navStatusData?.fix_status?.fix_valid ? 
                                                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} /> :
                                                        <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                                    }
                                                    <Typography variant="caption">Fix Valid</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    {navStatusData?.fix_status?.differential_solution ? 
                                                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} /> :
                                                        <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                                    }
                                                    <Typography variant="caption">DGPS</Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    {navStatusData?.fix_status?.carrier_solution_valid ? 
                                                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} /> :
                                                        <CancelIcon sx={{ color: 'error.main', fontSize: 16 }} />
                                                    }
                                                    <Typography variant="caption">RTK</Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                )}

                                {/* Constellation Summary Section */}
                                {navSatData && (
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 2, 
                                            backgroundColor: theme.palette.background.default,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <SignalIcon sx={{ mr: 1, color: theme.palette.info.main }} />
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                Constellation Summary
                                            </Typography>
                                        </Box>
                                        
                                        {formatConstellationSummary(navSatData)}
                                    </Paper>
                                )}

                                {/* Interference Monitor Section */}
                                {(() => {
                                    const integritySource = getInterferenceSource<IntegritySource>('integrity');
                                    const ubxSource = getInterferenceSource<UbxFlagsSource>('ubx_flags');
                                    const agcSource = getInterferenceSource<AgcDetectionSource>('agc_detection');
                                    const key = String(gnssDeviceId);
                                    const ubxDevice: UbxFlagsPerDevice | undefined = ubxSource?.per_device?.[key];
                                    const agcDevice: AgcPerDevice | undefined = agcSource?.per_device?.[key];
                                    const integrityAffected = integritySource?.affected_devices?.includes(gnssDeviceId) ?? false;

                                    const hasInterferenceData = integritySource || ubxSource || agcSource;

                                    // Traffic light for each section
                                    const integrityColor = !integritySource ? theme.palette.grey[500] : integrityAffected ? theme.palette.error.main : theme.palette.success.main;
                                    const ubxWorst = ubxDevice ? Math.max(ubxDevice.jamming_state, ubxDevice.spoofing_state, ubxDevice.spoof_det_state) : 0;
                                    const ubxColor = !ubxDevice ? theme.palette.grey[500] : getStateColor(ubxWorst);
                                    const agcColor = !agcDevice ? theme.palette.grey[500] : agcDevice.jamming ? theme.palette.error.main : theme.palette.success.main;

                                    return (
                                        <Paper
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mt: 2,
                                                backgroundColor: theme.palette.background.default,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <ShieldIcon sx={{ mr: 1, color: theme.palette.warning.main }} />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    Interference Monitor
                                                </Typography>
                                            </Box>

                                            {!hasInterferenceData ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    No interference data available
                                                </Typography>
                                            ) : (
                                                <Stack spacing={2}>
                                                    {/* Integrity */}
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                            <Box sx={{
                                                                width: 10, height: 10, borderRadius: '50%',
                                                                backgroundColor: integrityColor,
                                                                boxShadow: `0 0 5px ${integrityColor}`,
                                                                mr: 1, flexShrink: 0,
                                                            }} />
                                                            <Typography variant="caption" fontWeight={600}>Integrity</Typography>
                                                        </Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>
                                                            {!integritySource ? 'No data' : integrityAffected ? 'Anomaly detected' : 'OK'}
                                                        </Typography>
                                                    </Box>

                                                    <Divider />

                                                    {/* UBX Hardware */}
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                            <Box sx={{
                                                                width: 10, height: 10, borderRadius: '50%',
                                                                backgroundColor: ubxColor,
                                                                boxShadow: `0 0 5px ${ubxColor}`,
                                                                mr: 1, flexShrink: 0,
                                                            }} />
                                                            <Typography variant="caption" fontWeight={600}>UBX Hardware</Typography>
                                                        </Box>
                                                        {!ubxDevice ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>No data</Typography>
                                                        ) : (
                                                            <Stack spacing={1} sx={{ ml: 2.5 }}>
                                                                {/* Jamming + bar */}
                                                                <Box>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Typography variant="body2" color="text.secondary">Jamming</Typography>
                                                                        <Chip
                                                                            label={getJammingLabel(ubxDevice.jamming_state)}
                                                                            size="small"
                                                                            sx={{
                                                                                height: 20, fontSize: '0.7rem',
                                                                                backgroundColor: getStateColor(ubxDevice.jamming_state),
                                                                                color: '#fff',
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                                        <LinearProgress
                                                                            variant="determinate"
                                                                            value={(ubxDevice.jam_ind / 255) * 100}
                                                                            sx={{
                                                                                flexGrow: 1, height: 6, borderRadius: 3, mr: 1,
                                                                                backgroundColor: 'rgba(255,255,255,0.1)',
                                                                                '& .MuiLinearProgress-bar': {
                                                                                    backgroundColor: getJamIndColor(ubxDevice.jam_ind),
                                                                                    borderRadius: 3,
                                                                                },
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 24 }}>
                                                                            {ubxDevice.jam_ind}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                {/* Spoofing SEC-SIG */}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">Spoofing (SEC)</Typography>
                                                                    <Chip
                                                                        label={getSpoofingLabel(ubxDevice.spoofing_state)}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 20, fontSize: '0.7rem',
                                                                            backgroundColor: getStateColor(ubxDevice.spoofing_state),
                                                                            color: '#fff',
                                                                        }}
                                                                    />
                                                                </Box>
                                                                {/* Spoofing NAV */}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">Spoofing (NAV)</Typography>
                                                                    <Chip
                                                                        label={getSpoofingLabel(ubxDevice.spoof_det_state)}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 20, fontSize: '0.7rem',
                                                                            backgroundColor: getStateColor(ubxDevice.spoof_det_state),
                                                                            color: '#fff',
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Stack>
                                                        )}
                                                    </Box>

                                                    <Divider />

                                                    {/* AGC Detection */}
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                                            <Box sx={{
                                                                width: 10, height: 10, borderRadius: '50%',
                                                                backgroundColor: agcColor,
                                                                boxShadow: `0 0 5px ${agcColor}`,
                                                                mr: 1, flexShrink: 0,
                                                            }} />
                                                            <Typography variant="caption" fontWeight={600}>AGC Detection</Typography>
                                                            {agcSource && (
                                                                <Chip
                                                                    label={agcSource.baseline_ready ? 'Active' : 'Calibrating'}
                                                                    size="small"
                                                                    color={agcSource.baseline_ready ? 'success' : 'default'}
                                                                    variant="outlined"
                                                                    sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                        {!agcDevice ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>No data</Typography>
                                                        ) : !agcDevice.baseline_ready ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5, fontStyle: 'italic' }}>
                                                                Calibrating...
                                                            </Typography>
                                                        ) : agcDevice.jamming ? (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ ml: 2.5, color: getAgcClassificationColor(agcDevice.classification), fontWeight: 600 }}
                                                            >
                                                                {agcDevice.classification || 'Jamming detected'}
                                                            </Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5 }}>
                                                                Clean
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            )}
                                        </Paper>
                                    );
                                })()}

                                {/* Last Update */}
                                {lastUpdate && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, justifyContent: 'center' }}>
                                        <TimeIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">
                                            Last update: {format(new Date(lastUpdate), 'HH:mm:ss')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                py: 4,
                                color: 'text.secondary'
                            }}>
                                <SatelliteIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
                                <Typography variant="body2">
                                    No telemetry data available
                                </Typography>
                                <Typography variant="caption">
                                    Waiting for UBX messages...
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SatelliteIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6">
                        GNSS Receivers Status
                    </Typography>
                </Box>
                {realtimeData.lastUpdated && (
                    <Chip
                        label={`Last updated: ${format(new Date(realtimeData.lastUpdated), 'HH:mm:ss')}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Box>

            <Grid container spacing={3}>
                {/* Render cards for GNSS devices 1-4 */}
                {[1, 2, 3, 4].map(gnssDeviceId => renderGnssDeviceCard(gnssDeviceId))}
            </Grid>

            {/* Summary Statistics */}
            <Paper 
                elevation={0}
                sx={{
                    p: 3,
                    mt: 3,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography variant="h6" gutterBottom>
                    System Summary
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">
                                {Object.keys(realtimeData.ubx_telemetry || {}).length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Active Receivers
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="success.main">
                                {Object.values(realtimeData.ubx_telemetry || {})
                                    .filter(device => {
                                        try {
                                            const fixStatus = device['NAV-STATUS']?.data?.parsed_data?.fix_status;
                                            return fixStatus && typeof fixStatus === 'object' && fixStatus.fix_type === 3;
                                        } catch {
                                            return false;
                                        }
                                    })
                                    .length}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                3D Fix Active
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="info.main">
                                {Object.values(realtimeData.ubx_telemetry || {})
                                    .reduce((total, device) => {
                                        try {
                                            const satData = device['NAV-SAT']?.data?.parsed_data;
                                            const totalSats = satData && typeof satData === 'object' && 'constellation_summary' in satData ? 
                                                (satData as ConstellationData).constellation_summary.total_satellites : 0;
                                            return total + (typeof totalSats === 'number' ? totalSats : 0);
                                        } catch {
                                            return total;
                                        }
                                    }, 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Total Satellites
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="secondary.main">
                                {Object.values(realtimeData.ubx_telemetry || {})
                                    .reduce((total, device) => {
                                        try {
                                            const satData = device['NAV-SAT']?.data?.parsed_data;
                                            const usedSats = satData && typeof satData === 'object' && 'constellation_summary' in satData ? 
                                                (satData as ConstellationData).constellation_summary.satellites_used : 0;
                                            return total + (typeof usedSats === 'number' ? usedSats : 0);
                                        } catch {
                                            return total;
                                        }
                                    }, 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Satellites Used
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
};

export default GnssReceiversTab;